import React, { useState } from 'react';
import { NOTIFICATION_AUTO_HIDE_MS } from '../../constants/uiTiming';
import './TruncatedText.css';

interface TruncatedTextProps {
  text: string | null | undefined;
  maxLength?: number;
  maxLines?: number;
  className?: string;
  showCopyButton?: boolean;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  maxLength = 100,
  maxLines = 2,
  className = '',
  showCopyButton = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const displayText = text || '—';
  const shouldTruncate =
    typeof text === 'string' && (text.length > maxLength || text.split('\n').length > maxLines);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (text) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), NOTIFICATION_AUTO_HIDE_MS);
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (shouldTruncate) {
      setIsExpanded((prev) => !prev);
    }
  };

  if (displayText === '—') {
    return <span className={className}>—</span>;
  }

  return (
    <div className={`truncated-text ${className}`.trim()}>
      <div
        className={`truncated-text__content${isExpanded ? ' truncated-text__content--expanded' : ''}`}
        onClick={handleToggle}
        title={!isExpanded && shouldTruncate ? text : undefined}
        style={{
          cursor: shouldTruncate ? 'pointer' : 'default',
          ...(!isExpanded && shouldTruncate
            ? {
                display: '-webkit-box',
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }
            : {}),
        }}
      >
        {text}
      </div>
      {shouldTruncate ? (
        <div className="truncated-text__actions">
          <button
            type="button"
            className="truncated-text__toggle-btn"
            onClick={handleToggle}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
          {showCopyButton ? (
            <button
              type="button"
              className="truncated-text__copy-btn"
              onClick={handleCopy}
              title={copied ? 'Copied!' : 'Copy full text'}
              aria-label={copied ? 'Copied' : 'Copy full text'}
            >
              <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden />
            </button>
          ) : null}
        </div>
      ) : showCopyButton && text && text.length > 0 ? (
        <div className="truncated-text__actions">
          <button
            type="button"
            className="truncated-text__copy-btn"
            onClick={handleCopy}
            title={copied ? 'Copied!' : 'Copy text'}
            aria-label={copied ? 'Copied' : 'Copy text'}
          >
            <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default TruncatedText;
